'use client';

import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { ReactNode, useCallback, useContext } from 'react';

// 跳转类型枚举
enum JumpType {
  PRODUCT_GROUP = 1, // 商品分组
  PLATFORM_SKU = 2, // 平台sku
  SYSTEM_PAGE = 3, // 系统页面
  SPECIFIED_LINK = 4, // 指定链接
  POPUP = 5, // 弹框
  BACK_UP = 6, // 返回上一级
  CLOSE_POPUP = 7, // 关闭弹窗
}

// 跳转目标接口（统一 MagicJump 和 useJump 的类型）
export interface JumpTarget {
  jump_type?: number | string;
  Jump_target?: string | number; // 统一支持 string | number
  id?: string | number;
  shop_sku?: string;
  title?: string;
  Jump_title?: string;
  product_group?: {
    id?: string | number;
    name?: string;
    path?: string;
  };
  sku?: {
    shop_sku?: string;
  };
  isAllowClick?: boolean;
}

/**
 * 跳转处理Hook
 * 统一 MagicJump 和 useJump 的跳转逻辑
 *
 * 跳转类型说明：
 * - 1: 商品分组 -> /goods-list/{id}
 * - 2: 平台SKU -> /goods-detail/{shop_sku}
 * - 3: 系统页面 -> Jump_target作为路径
 * - 4: 指定链接 -> 外部链接或内部路径
 * - 5: 弹框 -> 触发弹框事件
 * - 6: 返回上一级 -> router.back()
 * - 7: 关闭弹窗 -> 触发关闭事件（需要回调）
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const toJumpTarget = useJump();
 *
 *   const handleClick = () => {
 *     toJumpTarget({
 *       jump_type: 2,
 *       Jump_target: 'ABC123',
 *       sku: { shop_sku: 'ABC123' }
 *     });
 *   };
 *
 *   return <button onClick={handleClick}>查看商品</button>;
 * }
 * ```
 */
// 生成跳转URL的纯函数（合并 MagicJump 和 useJump 的域名检查规则）
export function getJumpUrl(item?: JumpTarget | null): string {
  if (!item) {
    return '';
  }

  const { jump_type, Jump_target, product_group, sku } = item;

  if (!jump_type || !Jump_target) {
    return '';
  }

  let url = '';
  try {
    const jumpType = Number(jump_type);
    const jumpTarget = String(Jump_target);

    switch (jumpType) {
      case JumpType.PRODUCT_GROUP:
        url = `/goods-list/${product_group?.path || product_group?.id || jumpTarget}`;
        break;
      case JumpType.PLATFORM_SKU:
        url = `/goods-detail/${sku?.shop_sku || jumpTarget}`;
        break;
      case JumpType.SYSTEM_PAGE:
        url = jumpTarget;
        break;
      case JumpType.SPECIFIED_LINK:
        if (
          (jumpTarget.includes('zeelool.com') || jumpTarget.includes('xmslol.cn')) &&
          !jumpTarget.includes(env.NEXT_PUBLIC_HELP_CENTER_PATH.replace('https://', ''))
        ) {
          const href = new URL(jumpTarget);
          url = href.href.replace(href.origin, '');
        } else {
          url = jumpTarget;
        }
        break;
      case JumpType.POPUP:
        if (Number(jumpTarget) === 1) {
          url = 'register';
        } else if (Number(jumpTarget) === 2) {
          url = 'coupon';
        } else if (Number(jumpTarget) === 3) {
          url = 'announcement_popup';
        } else if (Number(jumpTarget) === 4) {
          url = 'dynamic_resource_popup';
        }
        break;
      default:
        url = '';
        break;
    }
  } catch (error) {
    logger.log(error, 'error-jumpUrl');
  }

  return url;
}

export function useJump() {
  const router = useRouter();
  const { navigate } = useAppNavigation();
  const setQuickCouponDialog = useStore((state) => state.setQuickCouponDialog);
  const setDynamicResourceDialog = useStore((state) => state.setDynamicResourceDialog);
  const { deviceType } = useContext(ResponsiveContext);
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
  const token = useStore((state) => state.token);

  // 执行跳转（基于 MagicJump 的逻辑，增强支持类型 6 和 7）
  const toJumpTarget = useCallback(
    (target: JumpTarget) => {
      // 检查是否允许点击
      if (target?.isAllowClick === false) {
        return;
      }

      // 兼容 jump_type 不存在的情况，给默认值 ''
      const jump_type = target?.jump_type ?? '';
      const Jump_target = target?.Jump_target ?? '';

      const jumpUrl = getJumpUrl(target);
      const jumpType = Number(jump_type);

      // 处理关闭弹窗类型
      if (jumpType === JumpType.CLOSE_POPUP) {
        return;
      }

      // 处理返回上一级类型
      if (jumpType === JumpType.BACK_UP) {
        router.back();
        return;
      }

      if (jumpType === JumpType.PRODUCT_GROUP && inAppH5) {
        const appGroupId = target.product_group?.id;

        if (appGroupId !== undefined && appGroupId !== null && appGroupId !== '') {
          navigate(`/goods-list/${appGroupId}`);
        }
        return;
      }

      // 处理不同类型的跳转
      if (
        [JumpType.PRODUCT_GROUP, JumpType.PLATFORM_SKU, JumpType.SYSTEM_PAGE].includes(jumpType)
      ) {
        // 内部路由跳转（navigate 内部已处理 App/Web 判断）
        if (jumpUrl) {
          navigate(jumpUrl);
        }
        return;
      }

      if (jumpType === JumpType.SPECIFIED_LINK) {
        // 指定链接处理
        if (jumpUrl.includes('http')) {
          window.location.href = jumpUrl;
        } else {
          // 内部链接（navigate 内部已处理 App/Web 判断）
          navigate(jumpUrl);
        }
        return;
      }

      if (jumpType === JumpType.POPUP) {
        // 弹框处理
        const jumpTarget = Number(Jump_target);

        if (inAppH5) {
          // App内处理
          if (jumpTarget === 1 && !token) {
            navigate('/login');
            return;
          }
          if (jumpTarget === 2) {
            appCommunicationManager.sendMessage(EAppCommunicationFuncName.SHOW_DIALOG, {
              dialogName: 'QuickRegistrationDialog',
              backToken: !!token,
              arguments: { eventType: '' },
            });
          }
          if (Number(Jump_target) === 4) {
            setDynamicResourceDialog({
              show: true,
              key: '4',
            });
          }
          return;
        }

        // Web端处理
        if (jumpTarget === 2) {
          setQuickCouponDialog({
            show: true,
            eventType: '',
            loginSituation: '',
            triggerType: 'manual',
          });
        }
        if (Number(Jump_target) === 4) {
          setDynamicResourceDialog({
            show: true,
            key: '4',
          });
        }
      }
    },
    [router, navigate, inAppH5, token, setQuickCouponDialog],
  );

  // 直接返回 toJumpTarget，保持向后兼容
  return toJumpTarget;
}

// 组件Props接口
interface MagicJumpProps {
  item?: JumpTarget | null;
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onBeforeJump?: (jumpUrl: string) => void;
  onJumpClick?: () => void;
  onClosePop?: () => void;
  [key: string]: any; // 支持其他HTML属性
  openInNewTab?: boolean; // 是否在新标签页打开
  prefetch?: boolean;
}

// 判断是否为空
const isEmpty = (value: any): boolean => {
  return value === null || value === undefined || value === '';
};

export default function MagicJump({
  item,
  children,
  className = '',
  style,
  onBeforeJump,
  onJumpClick,
  onClosePop,
  openInNewTab = false,
  isAllowClick = true,
  prefetch,
  ...props
}: MagicJumpProps) {
  const toJumpTarget = useJump();

  // 使用导出的 getJumpUrl 函数
  const jumpUrl = getJumpUrl(item);

  // 处理跳转
  const handleJump = () => {
    // 旧人群推荐配置只展示内容，避免触发关闭菜单或写入归因的回调。
    if (!isAllowClick || !item || Number(item.jump_type) === 8) {
      return;
    }

    const { jump_type } = item;
    const jumpType = Number(jump_type);

    // 触发 beforeJump 事件
    onBeforeJump?.(jumpUrl);
    onJumpClick?.();

    // 处理新标签页打开
    if (openInNewTab && typeof window !== 'undefined') {
      if (
        [
          JumpType.PRODUCT_GROUP,
          JumpType.PLATFORM_SKU,
          JumpType.SYSTEM_PAGE,
          JumpType.SPECIFIED_LINK,
        ].includes(jumpType)
      ) {
        if (jumpUrl) {
          window.open(jumpUrl, '_blank');
          return;
        }
      }
    }

    // 处理关闭弹窗
    if (jumpType === JumpType.CLOSE_POPUP) {
      onClosePop?.();
      return;
    }

    // 其他情况使用统一的跳转逻辑
    toJumpTarget(item);
  };

  // 如果没有配置跳转目标，只展示内容
  if (!item || isEmpty(item) || !jumpUrl) {
    return (
      <div className={className} style={style} {...props} onClick={handleJump}>
        {children}
      </div>
    );
  }

  const { jump_type } = item;

  // 根据跳转类型渲染不同的元素
  if (
    [
      JumpType.PRODUCT_GROUP,
      JumpType.PLATFORM_SKU,
      JumpType.SYSTEM_PAGE,
      JumpType.SPECIFIED_LINK,
    ].includes(Number(jump_type))
  ) {
    // 使用a标签，支持SEO
    return (
      <Link
        prefetch={prefetch}
        href={jumpUrl}
        className={className}
        style={{ ...style, color: 'inherit', textDecoration: 'none' }}
        {...props}
        // onClick不能让任何属性覆盖，否则会丢失功能！！
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleJump();
        }}
      >
        {children}
      </Link>
    );
  }

  // 其他类型使用div，添加hover效果
  return (
    <div
      className={`${className} ${[JumpType.POPUP, JumpType.CLOSE_POPUP].includes(Number(jump_type)) ? 'cursor-pointer' : ''}`}
      style={style}
      {...props}
      onClick={handleJump}
    >
      {children}
    </div>
  );
}
